// Typed REST client for portal-api (the Go backend shared with the mobile app).
//
// SOURCE OF TRUTH for the API contract: the mobile app's `lib/api.ts`
// (/Users/geof/Divako/GitLab/portal-app/lib/api.ts) + the Go `/me` handlers in
// the portal-api repo. The domain types below are copied from there — when the
// contract changes, re-sync rather than re-derive. See CLAUDE.md → "The backend".
//
// The one deliberate difference from mobile: auth. Mobile signs a bearer JWT
// client-side; the web uses the BFF session cookie (`credentials: 'include'`),
// so there are no Authorization headers here.

import { config } from './config';
import type { ProjectType } from './projectType';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Global sign-out hook, registered by the app shell. apiFetch calls it on a 401
// (e.g. the session was rotated or the account disabled) so the whole app
// can react in one place. Kept module-level so api.ts stays React-agnostic.
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...((init.headers as Record<string, string>) ?? {}),
  };

  // `credentials: 'include'` sends the httpOnly session cookie (BFF auth).
  const res = await fetch(config.apiBaseUrl + path, {
    ...init,
    headers,
    credentials: 'include',
  });

  const text = await res.text().catch(() => '');

  if (!res.ok) {
    if (res.status === 401) {
      onUnauthorized?.();
    }
    throw new ApiError(res.status, text || res.statusText);
  }

  if (res.status === 204 || text === '') {
    return undefined as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError(res.status, 'Invalid JSON: ' + text);
  }
}

/* -------------------------------------------------------------------------- */
/* Domain types (must match the Go backend's /me handlers)                    */
/* -------------------------------------------------------------------------- */

export type CategoryOrGroup = {
  type: 'g' | 'c';
  id: string;
  name: string;
  // The category a group belongs to (absent for categories). Shown after a
  // group name that is only a number — see groupDisplayName.
  category_name?: string | null;
  calculation_date?: string | null;
  // Move-out day for this group membership (app_category_group_user.
  // moved_out_date). Null for categories and for groups the user hasn't
  // moved out of. Bounds the occupancy window along with calculation_date.
  moved_out_date?: string | null;
  // The group's public share secret (app_category_group.share_code). With
  // the gid it forms the api.divako.io/share/<code> link (base64 of
  // {"g": gid, "c": share_code}) — latest readings as JSON + the Home
  // Assistant config. Absent for categories and codeless groups.
  share_code?: string | null;
  // The user's role on this group: admins get the Share button and invite and
  // remove viewers, viewers only see data. Read it through isAdmin(). Absent
  // for categories.
  role?: AccessRole;
  config?: Record<string, unknown> | null;
  project_name: string;
  project_pid: string;
  // The project's IANA timezone (app_project.config.timezone, Europe/Oslo
  // when unset). This group's days and months are read on its calendar: the
  // period windows are built in it and it goes back as `tz`. Absent from a
  // portal-api that predates it — read the data as UTC then (LEGACY_ZONE).
  project_timezone?: string;
  // True when this entry's project shows the Billing page: switched on in the
  // `application` project settings while the project has the Settlement
  // integration (app-api checks both). Absent otherwise. Read through
  // hasBilling().
  project_billing?: boolean;
  // The project's type (app_project.config.type): groups and categories are
  // worded after it — see wordingFor. Absent from an older portal-api, which
  // words them as Other does.
  project_type?: ProjectType;
};

export type UserProjectRef = {
  pid: string;
  name: string;
  // Project location, from app_project.config (set in the `application`
  // admin). `country` is an ISO-2 code, e.g. "NO".
  country?: string;
  municipality?: string[];
  latitude?: number | null;
  longitude?: number | null;
  // Project-configured notice shown to users who have no meters linked
  // to their account/group/category. Absent/empty when not configured.
  no_meters_message?: string;
};

export type Me = {
  uid: string;
  name: string;
  email: string;
  phone: string;
  language: string;
  timezone: string;
  notifications: Record<string, unknown>;
  config: Record<string, unknown>;
  projects: UserProjectRef[];
  categories_and_groups: CategoryOrGroup[];
};

// MeAlert is one currently-active alert (state=1 on the device). The
// backend computes `label` from metadata.custom_name when set, otherwise
// from the raw `name`.
//
// `acknowledged` is set once someone acknowledged the alert — a group admin
// in the portal or the app, or anyone in app.divako.io. It stays active until
// the device clears it, but no longer counts as needing attention (openAlerts).
export type MeAlert = {
  name: string;
  custom_name?: string;
  label: string;
  type?: string;
  date: string;
  acknowledged?: boolean;
  acknowledged_message?: string;
  // Inside its suppression window (set in app.divako.io); YYYY-MM-DD last
  // day when the window has one.
  suppressed?: boolean;
  suppressed_until?: string;
};

// An alert's level is its m_alert type, as app.divako.io colours it: alert
// (red — a real alarm), notif (amber), event (blue), message (grey). Any
// other value reads as a message, as it does there. The `notif` level is a
// device-side notice — not the user's notification inbox (getMyNotifications)
// nor their per-meter notification rules — and the web labels it "Notice".
export type AlertLevel = 'alert' | 'notif' | 'event' | 'message';
export const ALERT_LEVELS: AlertLevel[] = ['alert', 'notif', 'event', 'message'];
export const alertLevel = (type?: string | null): AlertLevel =>
  type === 'alert' || type === 'notif' || type === 'event' ? type : 'message';

// openAlerts is what app.divako.io counts as an active alarm and what the
// red badges count: alert level only — notices and events are not alarms —
// and neither acknowledged nor suppressed.
export const openAlerts = (alerts?: MeAlert[] | null): MeAlert[] =>
  (alerts ?? []).filter((a) => alertLevel(a.type) === 'alert' && !a.acknowledged && !a.suppressed);

export type MeterCard = {
  mid: string;
  name: string;
  unit: string;
  type: string; // 'sum' | 'average' | ''
  color: string;
  icon: string;
  serial: string;
  value: number | null;
  // 'consumption' = (last - first) inside the requested window, only set
  // for sum-type meters when both ends had a reading. 'last' = the
  // meter's most recent reading.
  kind: 'consumption' | 'last';
  updated_at?: string;
  // EUI of the meter's linked device, when it has one. Null/absent for
  // device-less meters.
  deveui?: string | null;
  // The linked device's friendly name (m_device.name). Only populated by
  // the backend when device mode is on.
  device_name?: string | null;
  // Currently-active alerts on this meter's device.
  alerts?: MeAlert[];
};

export type MeterReading = {
  date: string;
  value: number;
};

export type MeterGranularity = 'hour' | 'day' | 'month';

// One pre-aggregated chart point. The backend buckets readings server-side
// at the requested granularity so the client just renders. Empty buckets
// arrive with reading/consumption/average undefined — keep the slot so the
// x-axis stays evenly spaced.
export type MeterBucket = {
  date: string;
  reading?: number | null;
  consumption?: number | null;
  average?: number | null;
};

export type MeterDetailPeriod = {
  from: string;
  to: string;
  granularity: MeterGranularity;
  consumption?: number | null;
  average?: number | null;
  values: MeterReading[];
  buckets: MeterBucket[];
};

export type MeterDetail = {
  mid: string;
  name: string;
  unit: string;
  type: string; // 'sum' | 'avg' | ''
  color: string;
  icon: string;
  serial: string;
  last_value?: number | null;
  last_date?: string;
  // Earliest day the user can browse back to (move-in date). null → unbounded.
  min_date?: string | null;
  // Latest day the user can browse forward to (move-out date). null → no
  // upper bound. Also the ceiling the readings were clamped to server-side.
  max_date?: string | null;
  device_name?: string | null;
  // Surfaced so the client can call `/product` with the device's EUI.
  deveui?: string | null;
  // Currently-active alerts for the meter's device (state=1).
  alerts?: MeAlert[];
  period: MeterDetailPeriod;
  comparison?: MeterDetailPeriod | null;
};

// MeterProductInfo is fetched lazily from /me/meters/{mid}/product. All
// fields populate together (full product card available) or all stay null.
export type MeterProductInfo = {
  name?: string | null;
  image?: string | null;
  manufacturer?: string | null;
  manufacturer_logo?: string | null;
  model?: string | null;
  device_type?: string | null;
};

// Per-user, per-meter notification rules. Mirrors the legacy mobile portal's
// exact JSON shape at app_user.notifications.meters[mid] — the `application`
// notification cron reads the same keys/values, so the contract is
// deliberately stringly-typed:
//   enable    "1" (on) / "0" (off)
//   direction "0" (above) / "1" (below)
//   length    consumption: "24" | "12" (hours); update: "hour" | "day"
//   value     numeric, sent as a string
//   sent      managed by the cron; we write "0" as the initial baseline
export type ThresholdRule = { enable: string; direction: string; value: string; sent?: string };
export type ConsumptionRule = { enable: string; length: string; value: string; sent?: string };
export type UpdateRule = { enable: string; value: string; length: string; sent?: string };
export type PeriodRule = { enable: string; start: string; end: string; sent?: string };
export type SelectedAlarmRule = { enable: string; list: string[]; sent?: unknown };

export type MeterNotifyRules = {
  time?: string;
  threshold?: ThresholdRule;
  consumption?: ConsumptionRule;
  update?: UpdateRule;
  period?: PeriodRule;
  selected_alarm?: SelectedAlarmRule;
};

export type MeterNotifySettings = {
  rules: MeterNotifyRules;
  alarm_names: string[];
};

export type Notification = {
  id: number;
  subject: string;
  message: string;
  icon: string;
  color: string;
  date: string;
  read: number;
  parameters: unknown[];
  link: string;
  pid?: string;
  project?: { pid: string; name: string };
};

export type NotificationCounts = {
  total: number;
  unread: number;
};

export type UploadMeterPictureResponse = { id: string };

export type GroupShare = { share_code: string };
export type PasswordResetResponse = { status?: string };

/* -------------------------------------------------------------------------- */
/* Calls                                                                      */
/* -------------------------------------------------------------------------- */

export const getMe = () => apiFetch<Me>('/me');

export const getMyGroupMeters = (
  type: 'g' | 'c',
  id: string,
  range?: { from: string; to: string; tz?: string },
) => {
  const path = `/me/groups/${type}/${encodeURIComponent(id)}/meters`;
  if (!range) return apiFetch<MeterCard[]>(path);
  const qs = new URLSearchParams({ from: range.from, to: range.to });
  if (range.tz) qs.set('tz', range.tz);
  return apiFetch<MeterCard[]>(`${path}?${qs.toString()}`);
};

// generateGroupShareCode writes the group a fresh public share secret. It
// ALWAYS mints, so every link built from a previous code stops working: call it
// for a group that has no code yet, or behind a confirmation when the user
// deliberately rolls the token over.
export const generateGroupShareCode = (gid: string) =>
  apiFetch<GroupShare>(`/me/groups/g/${encodeURIComponent(gid)}/share-code`, { method: 'POST' });

/* -------------------------------------------------------------------------- */
/* Group access sharing                                                       */
/* -------------------------------------------------------------------------- */

// A group has one or more admins; everyone else is a viewer. Admins are made
// only in app.divako.io — this app can invite and remove viewers, nothing
// more. 'owner' is what an admin was called until 2026-09-23, which a
// portal-api that has not been redeployed may still send.
export type AccessRole = 'admin' | 'viewer' | 'owner';

export const isAdmin = (role?: AccessRole | null): boolean =>
  role === 'admin' || role === 'owner';

// One person on a group's access list — every member, with their role.
export type AccessPerson = {
  uid: string;
  name: string;
  email: string;
  // The day this person started seeing data (calculation_date), YYYY-MM-DD.
  since?: string | null;
  is_me: boolean;
  role: AccessRole;
};

export type GroupAccess = {
  // Whether the caller is an admin of the group. Viewers cannot manage
  // access, and the UI hides the Access tab entirely for them.
  can_manage: boolean;
  people: AccessPerson[];
};

export type GrantAccessResult = {
  uid: string;
  email: string;
  name: string;
  // True when the address had no Divako account and one was created — that is
  // the only case where the invitee receives a set-password email.
  created: boolean;
  since: string;
};

export const listGroupAccess = (gid: string) =>
  apiFetch<GroupAccess>(`/me/groups/g/${encodeURIComponent(gid)}/users`);

// Throws ApiError 409 when the person already has access and 429 when the
// group has hit its sharing limit — the two are separate statuses precisely so
// callers never have to parse the message text.
export const grantGroupAccess = (gid: string, email: string, name: string) =>
  apiFetch<GrantAccessResult>(`/me/groups/g/${encodeURIComponent(gid)}/users`, {
    method: 'POST',
    body: JSON.stringify({ email, name }),
  });

// Removes a viewer. Admins only; the backend answers 403 for an admin.
export const revokeGroupAccess = (gid: string, uid: string) =>
  apiFetch<void>(
    `/me/groups/g/${encodeURIComponent(gid)}/users/${encodeURIComponent(uid)}`,
    { method: 'DELETE' },
  );

export const getMyMeter = (
  mid: string,
  pid: string,
  // `tz` is the zone the window was built in (the group's project_timezone):
  // the backend buckets on its calendar.
  range?: { from: string; to: string; granularity?: MeterGranularity; tz?: string },
  // Group/category the meter was opened from. Sent so the backend clamps
  // the readings to that exact membership's [move-in, move-out] occupancy
  // window (the security boundary). Omit for deep links with no group
  // context — the backend then falls back to a project-wide window.
  group?: { type: 'g' | 'c'; id: string },
) => {
  const qs = new URLSearchParams({ pid });
  if (range) {
    qs.set('from', range.from);
    qs.set('to', range.to);
    if (range.granularity) qs.set('granularity', range.granularity);
    if (range.tz) qs.set('tz', range.tz);
  }
  if (group) {
    qs.set('gtype', group.type);
    qs.set('gid', group.id);
  }
  return apiFetch<MeterDetail>(`/me/meters/${encodeURIComponent(mid)}?${qs.toString()}`);
};

// getMyMeterProduct calls the lazy product-info endpoint. Pass `deveui`
// when known (it comes back in MeterDetail) so the backend skips its own
// metering-api round-trip.
export const getMyMeterProduct = (mid: string, pid: string, deveui?: string | null) => {
  const qs = new URLSearchParams({ pid });
  if (deveui) qs.set('deveui', deveui);
  return apiFetch<MeterProductInfo>(
    `/me/meters/${encodeURIComponent(mid)}/product?${qs.toString()}`,
  );
};

// AlarmEpisode is one alarm that has ended: when it went active and when it
// ended. `expired` means the device stopped reporting rather than the
// condition clearing.
export type AlarmEpisode = {
  name: string;
  custom_name?: string;
  label: string;
  type?: string;
  start: string;
  end: string;
  expired?: boolean;
  acknowledged?: boolean;
  acknowledged_message?: string;
};

// MeterAlarms feeds the alerts modal: the device's active alerts
// (acknowledged or not) and the ones that ended since `history_from` — the
// last 12 months, inside the user's occupancy window.
export type MeterAlarms = {
  active: MeAlert[];
  history: AlarmEpisode[];
  history_from: string;
  can_acknowledge: boolean;
};

export const getMeterAlarms = (mid: string, pid: string, group?: { type: 'g' | 'c'; id: string }) => {
  const qs = new URLSearchParams({ pid });
  if (group) {
    qs.set('gtype', group.type);
    qs.set('gid', group.id);
  }
  return apiFetch<MeterAlarms>(`/me/meters/${encodeURIComponent(mid)}/alerts?${qs.toString()}`);
};

// acknowledgeMeterAlert marks one of the meter's active alerts acknowledged,
// with an optional note, and returns the alert as it now stands. `gid` is the
// group the meter was opened from; the backend requires the user to be its
// admin. Throws ApiError 409 when the alert ended in the meantime and 403
// when the user may not acknowledge it.
export const acknowledgeMeterAlert = (
  mid: string,
  pid: string,
  gid: string,
  name: string,
  message: string,
) =>
  apiFetch<MeAlert>(
    `/me/meters/${encodeURIComponent(mid)}/alerts/${encodeURIComponent(name)}/acknowledge?` +
      new URLSearchParams({ pid, gid }).toString(),
    {
      method: 'POST',
      body: JSON.stringify({ message }),
    },
  );

export const getMyMeterNotifications = (mid: string, pid: string) =>
  apiFetch<MeterNotifySettings>(
    `/me/meters/${encodeURIComponent(mid)}/notifications?pid=${encodeURIComponent(pid)}`,
  );

// The backend read-modify-writes the user's whole notifications column, so we
// only send this meter's rules.
export const updateMyMeterNotifications = (mid: string, pid: string, rules: MeterNotifyRules) =>
  apiFetch<void>(
    `/me/meters/${encodeURIComponent(mid)}/notifications?pid=${encodeURIComponent(pid)}`,
    {
      method: 'PUT',
      body: JSON.stringify(rules),
    },
  );

export const getMyNotifications = (limit = 25, offset = 0, opts?: { unread?: boolean }) => {
  const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (opts?.unread) qs.set('unread', 'true');
  return apiFetch<Notification[]>(`/me/notifications?${qs.toString()}`);
};

export const getMyNotificationsCount = () =>
  apiFetch<NotificationCounts>('/me/notifications/count');

export const markNotificationRead = (id: number) =>
  apiFetch<void>(`/me/notifications/${id}/read`, { method: 'PUT' });

// uploadMeterPicture posts a base64-encoded JPEG/PNG; the backend forwards it
// to app-api's files endpoint linked to this meter. Returns the file uuid.
export const uploadMeterPicture = (mid: string, pid: string, base64: string, mimeType: string) =>
  apiFetch<UploadMeterPictureResponse>(
    `/me/meters/${encodeURIComponent(mid)}/picture?pid=${encodeURIComponent(pid)}`,
    {
      method: 'POST',
      body: JSON.stringify({ data: base64, mime_type: mimeType }),
    },
  );

// uploadMyPicture posts the signed-in user's new profile picture; it lands in
// app_user.avatar, which is what avatarUrl() serves — cache-bust after upload.
export const uploadMyPicture = (base64: string, mimeType: string) =>
  apiFetch<void>('/me/picture', {
    method: 'POST',
    body: JSON.stringify({ data: base64, mime_type: mimeType }),
  });

// updateMyProfile patches the account-level profile fields a user can edit —
// today just the IANA timezone. portal-api forwards it to app-api's
// self-service PATCH /users/{uid}, which validates the zone and writes the
// same app_user column the legacy portal's profile page did.
export const updateMyProfile = (fields: { timezone: string }) =>
  apiFetch<void>('/me', { method: 'PATCH', body: JSON.stringify(fields) });

// requestPasswordReset asks the backend to email the signed-in user a
// set-password link. Throttled server-side (~2 min) — a 429 ApiError whose
// body is `{"message":...,"seconds":N}` tells the caller how long to wait.
export const requestPasswordReset = () =>
  apiFetch<PasswordResetResponse>('/me/password-reset', { method: 'POST' });

// Public avatar endpoint (same one the platform + mobile app use). Append
// `?t=<ts>` to cache-bust after an upload.
export const avatarUrl = (uid: string, size: 'small' | 'large' = 'small') =>
  `https://app.divako.io/u/avatar/${encodeURIComponent(uid)}/${size}`;

/* -------------------------------------------------------------------------- */
/* Billing (Settlement)                                                       */
/* -------------------------------------------------------------------------- */

// A home's settled months from Divako Settlement, for the days the caller has
// had it — GET /me/groups/g/{gid}/billing. Only the home's owner, and only in
// a project that shows the Billing page (project_billing). Mirrors portal-api's
// handler/me_billing.go; types copied from the mobile lib/api.ts.

export type BillingLineKind = 'direct' | 'common' | 'correction';

// One line as the bill PDF prints it: `direct` is the home's own share,
// `common` its share of what no home's meter measured, `correction` a change
// to an earlier month. `quantity` is in `unit` for quantity_kind "usage", an
// allocator count for "count"; `rate` is the price per unit the bill prints.
export type BillingLine = {
  name: string;
  kind: BillingLineKind;
  fee: boolean;
  method: string;
  quantity_kind: 'usage' | 'count' | 'none';
  quantity?: number;
  unit?: string;
  rate?: number;
  vat_rate?: number;
  vat_shown: boolean;
  amount?: number;
  estimated: boolean;
  corrects?: { reason: 'bill' | 'handover'; months: string[]; handover_date?: string };
};

// One bill of the month. kind: a meter category, or "fee" for an unmetered
// charge.
export type BillingCategory = {
  code: string;
  name: string;
  kind: 'water' | 'energy' | 'heat' | 'gas' | 'other' | 'fee';
  amount?: number;
  lines: BillingLine[];
};

// A meter of the home, read at the edges of the caller's days of the month.
export type BillingMeter = {
  name: string;
  serial?: string;
  kind?: string;
  unit?: string;
  opening?: number;
  opening_date?: string;
  closing?: number;
  closing_date?: string;
  usage?: number;
};

// One settled month: from..to are the caller's own days of it. money false =
// the association settles use only (no amounts). vat is what the bill
// specifies, absent when it specifies none.
export type BillingSettlement = {
  label: string;
  from: string;
  to: string;
  period_from: string;
  period_to: string;
  settled_on?: string;
  pre_settlement: boolean;
  money: boolean;
  currency: string;
  total?: number;
  vat?: number;
  categories: BillingCategory[];
  meters: BillingMeter[];
  // The association's average measured use of a bill per m² that month.
  averages: { category_code: string; unit: string; per_m2: number }[];
};

// connected false: Settlement has not taken the home in yet (nothing to show).
export type Billing = {
  connected: boolean;
  unit?: { name: string; area_m2?: number };
  settlements: BillingSettlement[];
};

export const getMyBilling = (gid: string) =>
  apiFetch<Billing>(`/me/groups/g/${encodeURIComponent(gid)}/billing`);

// The month's bill as a PDF, served inline. A plain link: opening it is a
// top-level navigation, which carries the session cookie on its own.
export const myBillPdfUrl = (gid: string, label: string) =>
  `${config.apiBaseUrl}/me/groups/g/${encodeURIComponent(gid)}/billing/${encodeURIComponent(label)}/bill.pdf`;
